/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

//Core imports
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { Col, Row } from 'antd';

//Misc imports
import { moveArrayImmutable } from '../../utils/array/move';

/**
 * Draggable and Sortable grid layout. Uses 'react-sortable-hoc' which is a lib supporting dnd in grid layouts. (https://github.com/clauderic/react-sortable-hoc)
 * !ATTENTION! See https://github.com/clauderic/react-sortable-hoc/issues/877 for troubleshooting when upgrading to React 18 (if not updated)
 * !!ATTENTION!! When this project updates to React 19 this lib will no longer work due to removal of findDOMNode from React.
 * When that time comes, see if 'react-beautiful-dnd' (which we already use) or their very well maintained fork '@hello-pangea/dnd' has added grid support.
 * (Pangea team has this feature on their roadmap). Switching to '@hello-pangea/dnd' would be the best course of action.
 *
 * @example
 * Basic usage DraggableGrid
 *
 * import DraggableGrid from '../../../UI/DraggableGrid';
 *
 * <DraggableGrid
 *    columns={2}
 *    items={yourItemList}
 *    onDragEnd={yourHandlerFunction}
 *    renderItem={(item, index) => (
 *         <YourAwesomeComponentHere item={item} index={index}/>
 *         )}
 *    renderPlaceholder={() => <YourAwesomePlaceholder/>}
 * />
 *
 * @param columns Number of columns for the grid layout
 * @param items Items to render
 * @param renderItem Function to render your UI of each item
 * @param renderPlaceholder If need to add a component at the end of the grid e.g. add more etc
 * @param onDragEnd Function that handles the updated items
 * @returns {JSX.Element}
 */
const DraggableGrid = ({ columns = 2, items = [], renderItem, renderPlaceholder, onDragEnd }) => {
  const [localItems, setLocalItems] = useState(items);

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const SortableListItem = SortableElement(({ item, sortIndex }) => (
    <Col
      span={24/columns}
      key={sortIndex}
      className={'grid-item'}
    >
      {renderItem ? renderItem(item, sortIndex) : <div>{item?.id}</div>}
    </Col>
  ));

  const SortableList = SortableContainer(({ items }) => {
    return (
      <Row gutter={[24, 16]} className={'grid-container'} justify={items.length > 0 ? 'start' : 'center'}>
        {items.map((item, index) => (
          <SortableListItem
            key={`item-${index}`}
            index={index}
            sortIndex={index}
            item={item}
          />
        ))}
        <Col span={items.length > 0 ? 24/columns : 12}>{renderPlaceholder ? renderPlaceholder() : null}</Col>
      </Row>
    );
  });

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newList = moveArrayImmutable(localItems, oldIndex, newIndex);
    setLocalItems(newList);
    if (onDragEnd) {
      onDragEnd(newList);
    }
  };

  return (
    <SortableList
      distance={1} //Allow inner element events when not dragging
      helperClass={'sortableHelper'}
      axis="xy"
      transitionDuration={0}
      items={localItems}
      onSortEnd={onSortEnd}
    />
  );
};

DraggableGrid.propTypes = {
  onDragEnd: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired,
  renderItem: PropTypes.func.isRequired,
  columns: PropTypes.number,
  renderPlaceholder: PropTypes.func
};

export default DraggableGrid;
